import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { useClasses } from '@/features/classes';
import { fromDateTimeLocalValue } from '@/lib/format';
import { useCreateProject } from '../queries';

type Translate = (key: string) => string;

const makeSchema = (t: Translate) =>
  z.object({
    title: z.string().min(1, t('projects:validation.titleRequired')),
    school_class: z.string().min(1, t('projects:validation.classRequired')),
    due_at: z.string(),
    grading_mode: z.enum(['team', 'individual']),
    max_score: z.string().min(1, t('projects:validation.maxScoreRequired')),
  });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect a class (used from the class detail page). */
  defaultClass?: string;
}

/**
 * Create-project dialog (admin/teacher). Lands on the project page so the
 * teacher can write the brief and pick the team straight away.
 */
export function CreateProjectDialog({
  open,
  onOpenChange,
  defaultClass,
}: CreateProjectDialogProps) {
  const { t } = useTranslation(['projects', 'common']);
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const classes = useClasses({ is_archived: false, page_size: 100 }, { enabled: open });

  const schema = useMemo(() => makeSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      school_class: defaultClass ?? '',
      due_at: '',
      grading_mode: 'team',
      max_score: '100',
    },
  });

  const options = classes.data?.results ?? [];

  async function onSubmit(values: FormValues) {
    try {
      const created = await createProject.mutateAsync({
        title: values.title,
        school_class: values.school_class,
        due_at: fromDateTimeLocalValue(values.due_at),
        grading_mode: values.grading_mode,
        max_score: values.max_score,
      });
      toast.success(t('projects:create.success'));
      form.reset();
      onOpenChange(false);
      navigate(`/projects/${created.public_id}`);
    } catch (e) {
      const err = e as ApiError;
      const fe = err?.fieldErrors;
      if (fe?.title) form.setError('title', { message: fe.title.join(' ') });
      if (fe?.school_class) form.setError('school_class', { message: fe.school_class.join(' ') });
      if (fe?.max_score) form.setError('max_score', { message: fe.max_score.join(' ') });
      if (!fe?.title && !fe?.school_class && !fe?.max_score) {
        form.setError('root', {
          message: fe?.non_field_errors?.join(' ') ?? err?.message ?? t('projects:create.failed'),
        });
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('projects:create.title')}</DialogTitle>
          <DialogDescription>{t('projects:create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('projects:create.titleLabel')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('projects:create.titlePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="school_class"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('projects:create.classLabel')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('projects:create.classPlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {options.map(cls => (
                        <SelectItem key={cls.public_id} value={cls.public_id}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {options.length === 0 && !classes.isLoading ? (
                    <FormDescription>{t('projects:create.noClasses')}</FormDescription>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="grading_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('projects:create.gradingModeLabel')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="team">{t('projects:gradingMode.team')}</SelectItem>
                        <SelectItem value="individual">
                          {t('projects:gradingMode.individual')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="max_score"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('projects:create.maxScoreLabel')}</FormLabel>
                    <FormControl>
                      <Input type="number" min="0.01" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="due_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('projects:create.dueLabel')}</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {t('projects:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
